import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Student } from "@/common/types/student";
import { Buttons } from "@/components/atoms/Buttons";
import { showAlert } from "@/components/containers/Alert";
import { InputGroup } from "@/components/molecules/InputGroup";
import { TelControl } from "@/components/containers/Controls/TelControl";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputSelect } from "@/components/atoms/Inputs/InputSelect";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { RadioControl } from "@/components/containers/Controls/RadioControl";
import { GET_USER_INFO } from "@/common/querykeys";
import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";
import { Controller, useForm, useWatch } from "react-hook-form";

const schema = yup
  .object({
    student: yup
      .object({
        name: yup.string().nullable().required("학생 이름은 필수 입력입니다."),
        birth: yup
          .date()
          .typeError(
            "생년월일은 YYYY-MM-DD 형식이어야 하며, 유효한 날짜여야 합니다."
          )
          .required("생년월일은 필수 입력입니다."),
        gender: yup.string().nullable().required("성별은 필수 입력입니다."),
        tel: yup
          .string()
          .length(
            13,
            "연락처는 000-0000-0000 형식이며, 13자에 맞게 작성하셔야 합니다."
          )
          .required("연락처는 필수 입력입니다."),
        schoolId: yup.string().required("학교 ID는 필수 입력입니다."),
        entityType: yup.string().required("참여 유형은 필수 입력입니다."),
        organizationName: yup.string().when("entityType", {
          is: "organization",
          then: (schema) =>
            schema.required("소속 기관 이름은 필수 입력입니다."),
          otherwise: (schema) => schema.strip(),
        }),
        studentIdNumber: yup
          .string()
          .max(20, "학번은 최대 20자까지 입력 가능합니다."),
        schoolType: yup.string().required("학교 구분은 필수 입력입니다."),
      })
      .required("학생 정보는 필수 입력입니다."),
  })
  .required();

export const StudentInfoEditPage = () => {
  /** 📦 State Store */
  const navigate = useNavigate();

  /** 📡 API Layer */
  // 학생 정보 데이터 조회
  const { data: studentInfo } = useQuery<Student>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Student>({
        method: "GET",
        url: "/student/info",
      });
    },
  });

  // 학생 정보 수정
  const { mutateAsync: updateInfo } = useMutation({
    mutationFn: (data: {
      id: string;
      userId: string;
      name: string;
      birth: Date;
      gender: string;
      tel: string;
      studentIdNumber: string;
      entityType: string;
      schoolId: string;
      schoolType: string;
      organizationName: string;
    }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "/student",
        data: data,
      });
    },
  });

  /** 📝 Form State Definition */
  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      student: {
        name: studentInfo?.name,
        birth: studentInfo?.birth,
        gender: studentInfo?.gender,
        tel: studentInfo?.tel,
        schoolId: studentInfo?.school.name,
        schoolType: studentInfo?.school.type,
        entityType: studentInfo?.entityType,
        organizationName: studentInfo?.organizationName,
        studentIdNumber: studentInfo?.studentIdNumber,
      },
    },
  });

  /** 🚀 Action Handlers */
  const onSubmit = async (data: any) => {
    try {
      if (studentInfo) {
        await updateInfo({
          id: studentInfo.id,
          userId: studentInfo.userId,
          name: data.student.name,
          birth: data.student.birth ? new Date(data.student.birth) : new Date(),
          gender: data.student.gender,
          tel: data.student.tel,
          studentIdNumber: data.student.studentIdNumber,
          schoolId: data.student.schoolId,
          schoolType: data.student.schoolType,
          entityType: data.student.entityType,
          organizationName: data.student.organizationName,
        });
        navigate("/setting/user");
      } else {
        showAlert("잠시 후 다시 시도해주세요.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  /** 👀 Form Value Observation/Subscription */
  const entityType = useWatch({ control, name: "student.entityType" });
  const schoolType = useWatch({
    control,
    name: "student.schoolType",
  });

  /** 📌 Value Reference */
  const prevSchoolTypeRef = useRef<string | undefined>(
    studentInfo?.school.type
  );

  /** 🔄 Side Effect Management */
  useEffect(() => {
    if (
      schoolType === "etc" &&
      prevSchoolTypeRef.current &&
      prevSchoolTypeRef.current !== "etc"
    ) {
      setValue("student.schoolId", "");
    }
    prevSchoolTypeRef.current = schoolType;
  }, [schoolType, setValue]);

  useEffect(() => {
    if (entityType !== "organization") {
      setValue("student.organizationName", "");
    }
  }, [entityType, setValue]);

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          color="primary100"
          layout="text"
          label="설정"
          icon={<IconArrow />}
          onClick={() => window.history.back()}
        />
      }
      pageTitle="내 정보 수정"
    >
      <Form
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="저장"
            disabled={false}
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="취소"
            disabled={false}
            onClick={() => window.history.back()}
          />
        }
      >
        <>
          <FormInputBox formTitle="회원정보 수정">
            <>
              {/* 이름 */}
              <Controller
                name="student.name"
                control={control}
                render={({ field }) => (
                  <TextControl
                    control={control}
                    {...field}
                    label="이름"
                    placeholder="이름을 입력하세요."
                    required={true}
                  />
                )}
              />

              {/* 생년월일 */}
              <Controller
                name="student.birth"
                control={control}
                render={({ field }) => (
                  <TextControl
                    control={control}
                    {...field}
                    type="date"
                    value={
                      field.value
                        ? new Date(field.value).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const dateString = e.target.value;
                      field.onChange(
                        dateString ? new Date(dateString) : undefined
                      );
                    }}
                    label="생년월일"
                    placeholder="생년월일을 입력하세요."
                    required={true}
                  />
                )}
              />

              {/* 성별 */}
              <Controller
                name="student.gender"
                control={control}
                render={({ field }) => (
                  <RadioControl
                    control={control}
                    {...field}
                    size="lg"
                    label="성별"
                    data={[
                      { value: "M", displayExpr: "남" },
                      { value: "F", displayExpr: "여" },
                    ]}
                    required={true}
                  />
                )}
              />

              {/* 연락처 */}
              <Controller
                name="student.tel"
                control={control}
                render={({ field }) => (
                  <TelControl
                    control={control}
                    type="tel"
                    {...field}
                    label="연락처"
                    placeholder="000-0000-0000"
                    required={true}
                  />
                )}
              />

              {/* 참여 유형 & 기관명 */}
              <InputGroup direction="row" gridColumn="40% calc(60% - 4px)">
                <>
                  <Controller
                    name="student.entityType"
                    control={control}
                    render={({ field }) => (
                      <RadioControl
                        control={control}
                        {...field}
                        size="lg"
                        label="참여 유형"
                        required={true}
                        data={[
                          { value: "individual", displayExpr: "개인" },
                          { value: "organization", displayExpr: "단체" },
                        ]}
                      />
                    )}
                  />
                  <Controller
                    name="student.organizationName"
                    control={control}
                    render={({ field }) => (
                      <TextControl
                        control={control}
                        {...field}
                        placeholder="참여 기관을 입력하세요."
                        required={true}
                        disabled={entityType !== "organization"}
                      />
                    )}
                  />
                </>
              </InputGroup>

              {/* 학교 구분 & 학교명 */}
              <InputGroup direction="row" gridColumn="40% calc(60% - 4px)">
                <>
                  <Controller
                    name="student.schoolType"
                    control={control}
                    render={({ field }) => (
                      <InputSelect
                        {...field}
                        label="학교명"
                        placeholder="학교 구분"
                        required={true}
                        options={[
                          { label: "학교 구분", value: "" },
                          { label: "초등학교", value: "elementary" },
                          { label: "중학교", value: "middle" },
                          { label: "기타", value: "etc" },
                        ]}
                      />
                    )}
                  />
                  <Controller
                    name="student.schoolId"
                    control={control}
                    render={({ field }) =>
                      schoolType === "etc" ? (
                        <TextControl
                          control={control}
                          {...field}
                          placeholder="학교/소속명을 입력하세요."
                          required={true}
                        />
                      ) : (
                        <TextControl
                          control={control}
                          {...field}
                          placeholder="학교명을 입력하세요."
                          required={true}
                        />
                      )
                    }
                  />
                </>
              </InputGroup>

              {/* 학번 */}
              <Controller
                name="student.studentIdNumber"
                control={control}
                render={({ field }) => (
                  <TextControl
                    control={control}
                    {...field}
                    label="학번"
                    placeholder="학번을 입력하세요."
                    required={true}
                  />
                )}
              />
            </>
          </FormInputBox>
        </>
      </Form>
    </MainTemplate>
  );
};
